import type { ReactNode } from 'react';
import * as s from './DeltaIndicator.css';

export type DeltaFormat = 'absolute' | 'signed' | 'percent';
export type DeltaGlyph = 'arrow' | 'sign' | 'caret' | 'none';
export type DeltaSize = 'sm' | 'md';
export type DeltaTone = 'up' | 'down' | 'flat';
export type DeltaSemantics = 'default' | 'spend';

export interface DeltaIndicatorProps {
  /** 숫자 mode: 자동으로 tone 결정 + format 적용. null/undefined 면 렌더 안 함. */
  value?: number | null;
  /** preformatted mode: tone 직접 지정 (caller 가 label 으로 텍스트 전달). */
  tone?: DeltaTone;
  /** 텍스트 override — 숫자 자동 포맷 대신 caller 가 전달한 label 표시 (예: '+12분'). */
  label?: ReactNode;
  format?: DeltaFormat;
  glyph?: DeltaGlyph;
  size?: DeltaSize;
  /** 색 의미론 — 'spend'(지출 도메인): 증가=주의(amber)/감소=안정(green). 기본 'default'(증가=green). */
  semantics?: DeltaSemantics;
  ariaLabel?: string;
  className?: string;
}

const ARROW = { up: '▲', down: '▼', flat: '─' } as const;
const SIGN = { up: '+', down: '−', flat: '' } as const;
const CARET = { up: '↑', down: '↓', flat: '·' } as const;

function pickTone(value: number): DeltaTone {
  if (value > 0) return 'up';
  if (value < 0) return 'down';
  return 'flat';
}

function pickGlyph(g: DeltaGlyph, t: DeltaTone): string {
  if (g === 'none') return '';
  if (g === 'sign') return SIGN[t];
  if (g === 'caret') return CARET[t];
  return ARROW[t];
}

function formatValue(value: number, format: DeltaFormat, tone: DeltaTone): string {
  const abs = Math.abs(value);
  const num = format === 'percent' ? `${abs}%` : `${abs}`;
  if (format === 'signed' && tone === 'up') return `+${num}`;
  if (format === 'signed' && tone === 'down') return `−${num}`;
  return num;
}

function defaultAriaLabel(
  tone: DeltaTone,
  value: number,
  format: DeltaFormat,
  label: ReactNode
): string {
  const direction = tone === 'up' ? '상승' : tone === 'down' ? '하락' : '변동 없음';
  if (tone === 'flat') return direction;
  // 화면 글자를 그대로 읽는다 — 반올림 전 값(14.0175…)을 낭독하지 않게. 방향은 direction 이 말하니 부호만 뗀다.
  if (typeof label === 'string') return `${direction} ${label.replace(/^[+−-]/, '')}`;
  const num = Math.abs(value) + (format === 'percent' ? '%' : '');
  return `${direction} ${num}`;
}

export function DeltaIndicator({
  value,
  tone: toneProp,
  label,
  format = 'absolute',
  glyph = 'arrow',
  size = 'sm',
  semantics = 'default',
  ariaLabel,
  className,
}: DeltaIndicatorProps) {
  const hasValue = value !== null && value !== undefined;
  const hasPreset = toneProp !== undefined || label !== undefined;
  if (!hasValue && !hasPreset) return null;

  const tone: DeltaTone = toneProp ?? (hasValue ? pickTone(value) : 'flat');
  const glyphChar = pickGlyph(glyph, tone);
  const text = label ?? (hasValue ? formatValue(value, format, tone) : '');

  const computedAriaLabel =
    ariaLabel ??
    (hasValue
      ? defaultAriaLabel(tone, value, format, label)
      : tone === 'up'
        ? '상승'
        : tone === 'down'
          ? '하락'
          : '변동 없음');

  const toneCls = semantics === 'spend' ? s.toneSpend[tone] : s.tone[tone];

  return (
    <span
      role="status"
      aria-label={computedAriaLabel}
      className={`${s.indicator({ size })} ${toneCls}${className ? ` ${className}` : ''}`}
    >
      {glyphChar && <span aria-hidden="true">{glyphChar}</span>}
      {text !== '' && <span>{text}</span>}
    </span>
  );
}
